import React, { useEffect, useMemo, useState } from "react";
import {
  Tabs,
  Tab,
  TextField,
  MenuItem,
  Button,
  Typography,
} from "@mui/material";
import {
  useGetPendingCallingRequestsQuery,
  useSubmitCallingFeedbackMutation,
  useGetMissingAddressesQuery,
} from "../redux/crmApi";
import CrmAddressTab from "./CrmAddressTab";

const CallingRequestsPage = () => {
  const [tabValue, setTabValue] = useState(0);
  const [expanded, setExpanded] = useState(null);
  const [feedback, setFeedback] = useState("");
  const [response, setResponse] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [sortBy, setSortBy] = useState("name");
  const [sortOrder, setSortOrder] = useState("asc");
  const [currentPage, setCurrentPage] = useState(1);
  const [answers, setAnswers] = useState({});
  const requestsPerPage = 10;

  const { data, isLoading, error } = useGetPendingCallingRequestsQuery();
  const {
    data: missingAddressData,
    isLoading: missingAddressLoading,
    error: missingAddressError,
  } = useGetMissingAddressesQuery(); // Fetch missing addresses

  const pending = useMemo(() => data?.pending || [], [data]);
  const completed = useMemo(() => data?.completed || [], [data]);

  const missingAddresses = useMemo(
    () => missingAddressData || [],
    [missingAddressData],
  );

  useEffect(() => {
    if (!isLoading && pending.length > 0) {
      const initialAnswers = {};
      pending.forEach((request) => {
        initialAnswers[request.calling_requests_id] = {};
        request.questions_array.forEach((question, index) => {
          initialAnswers[request.calling_requests_id][index] =
            question.feedback || ""; // Load stored feedback
        });
      });
      setAnswers(initialAnswers);
    }
  }, [pending, isLoading]);
  const handleExpand = (request) => {
    setExpanded(
      expanded === request.calling_requests_id
        ? null
        : request.calling_requests_id,
    );
    setResponse(request.status || "pending"); // Set response to the current request's status
  };

  // const {
  //   data: { pending = [], completed = [] } = {},
  //   isLoading,
  //   error
  // } = useGetPendingCallingRequestsQuery();

  const [submitFeedback] = useSubmitCallingFeedbackMutation();

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
    setCurrentPage(1);
    setExpanded(null);
  };

  const handleAnswerChange = (requestId, questionIndex, value) => {
    setAnswers((prev) => ({
      ...prev,
      [requestId]: {
        ...prev[requestId],
        [questionIndex]: value,
      },
    }));
  };

  const handleSubmitFeedback = async (request) => {
    const feedbackEntries = request.questions_array.map((question, index) => ({
      feedback_id: question.feedback_id,
      feedback: answers[request.calling_requests_id]?.[index] || "", // Get saved feedback or empty string
    }));

    // Ensure at least one answer is filled
    if (feedbackEntries.every((entry) => !entry.feedback) || !response) {
      alert(
        "Please provide feedback for at least one question and select a response status.",
      );
      return;
    }

    try {
      await submitFeedback({
        calling_requests_id: request.calling_requests_id, // Ensure backend can identify the request
        feedback: feedbackEntries,
        status: response,
      }).unwrap();

      alert("Feedback submitted successfully.");

      // Clear answers for this request after submission
      setAnswers((prev) => ({ ...prev, [request.calling_requests_id]: {} }));
      setResponse("");
    } catch (error) {
      console.error("Failed to submit feedback:", error);
      alert("Failed to submit feedback.");
    }
  };

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
  };

  if (isLoading) return <div>Loading calling requests...</div>;
  if (error) return <div>Error loading calling requests</div>;

  // Get current requests based on tab
  const currentRequests = tabValue === 0 ? pending : completed;

  // Filter and sort requests
  const filteredRequests = currentRequests.filter((request) =>
    request.name.toLowerCase().includes(searchTerm.toLowerCase()),
  );

  const sortedRequests = [...filteredRequests].sort((a, b) => {
    const valA = a[sortBy];
    const valB = b[sortBy];
    return sortOrder === "asc"
      ? typeof valA === "string"
        ? valA.localeCompare(valB)
        : valA - valB
      : typeof valB === "string"
        ? valB.localeCompare(valA)
        : valB - valA;
  });

  // Pagination
  const indexOfLastRequest = currentPage * requestsPerPage;
  const indexOfFirstRequest = indexOfLastRequest - requestsPerPage;
  const paginatedRequests = sortedRequests.slice(
    indexOfFirstRequest,
    indexOfLastRequest,
  );
  const totalPages = Math.ceil(sortedRequests.length / requestsPerPage);

  return (
    <div
      style={{
        padding: "20px",
        fontFamily: "Arial, sans-serif",
        backgroundColor: "#f5f5f5",
        minHeight: "100vh",
      }}
    >
      <Typography
        variant="h4"
        sx={{ mb: 3, fontWeight: "bold", color: "#333" }}
      >
        {tabValue === 2 ? "Player Address Management" : "Calling Requests"}
      </Typography>

      {/* Search and Sort */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: "20px",
        }}
      >
        <input
          type="text"
          placeholder="Search by name..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          style={{
            padding: "10px",
            borderRadius: "8px",
            border: "1px solid #ddd",
            width: "300px",
            fontSize: "16px",
            outline: "none",
            boxShadow: "0 2px 4px rgba(0, 0, 0, 0.1)",
          }}
        />

        <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
          <label style={{ fontSize: "16px", fontWeight: "500" }}>
            Sort by:
          </label>
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            style={{
              padding: "8px",
              borderRadius: "8px",
              border: "1px solid #ddd",
              fontSize: "16px",
              outline: "none",
              cursor: "pointer",
            }}
          >
            <option value="name">Name</option>
            <option value="player_id">Player ID</option>
            {tabValue !== 2 && <option value="score">Score</option>}
            {tabValue !== 2 && (
              <option value="country_code">Country Code</option>
            )}
          </select>
          <button
            onClick={() => setSortOrder(sortOrder === "asc" ? "desc" : "asc")}
            style={{
              padding: "8px 16px",
              borderRadius: "8px",
              border: "none",
              backgroundColor: "#50868f",
              color: "#fff",
              fontSize: "16px",
              cursor: "pointer",
            }}
          >
            {sortOrder === "asc" ? "⬆️ Ascending" : "⬇️ Descending"}
          </button>
        </div>
      </div>

      {/* Tabs */}
      <Tabs value={tabValue} onChange={handleTabChange} sx={{ mb: 3 }}>
        <Tab label={`Pending (${pending.length})`} />
        <Tab label={`Completed (${completed.length})`} />
        <Tab label="Add Address" />
      </Tabs>

      {/* Tab Content */}
      {tabValue === 2 ? (
        <CrmAddressTab
          players={missingAddresses}
          searchTerm={searchTerm}
          sortBy={sortBy}
          sortOrder={sortOrder}
          currentPage={currentPage}
          requestsPerPage={requestsPerPage}
          onPageChange={handlePageChange}
        />
      ) : (
        <>
          {/* Pending/Completed Table */}
          <div
            style={{
              borderRadius: "8px",
              overflow: "hidden",
              boxShadow: "0 4px 8px rgba(0, 0, 0, 0.1)",
            }}
          >
            <table
              style={{
                width: "100%",
                borderCollapse: "collapse",
                backgroundColor: "#fff",
              }}
            >
              <thead>
                <tr>
                  <th
                    style={{
                      backgroundColor: "#50868f",
                      color: "#fff",
                      padding: "16px",
                      textAlign: "left",
                    }}
                  >
                    Player ID
                  </th>
                  <th
                    style={{
                      backgroundColor: "#50868f",
                      color: "#fff",
                      padding: "16px",
                      textAlign: "left",
                    }}
                  >
                    Name
                  </th>
                  <th
                    style={{
                      backgroundColor: "#50868f",
                      color: "#fff",
                      padding: "16px",
                      textAlign: "left",
                    }}
                  >
                    Country Code
                  </th>
                  <th
                    style={{
                      backgroundColor: "#50868f",
                      color: "#fff",
                      padding: "16px",
                      textAlign: "left",
                    }}
                  >
                    Phone
                  </th>
                  <th
                    style={{
                      backgroundColor: "#50868f",
                      color: "#fff",
                      padding: "16px",
                      textAlign: "left",
                    }}
                  >
                    Score
                  </th>
                  <th
                    style={{
                      backgroundColor: "#50868f",
                      color: "#fff",
                      padding: "16px",
                      textAlign: "left",
                    }}
                  >
                    Reason
                  </th>
                  <th
                    style={{
                      backgroundColor: "#50868f",
                      color: "#fff",
                      padding: "16px",
                      textAlign: "left",
                    }}
                  >
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {paginatedRequests.map((request) => (
                  <React.Fragment key={request.feedback_id}>
                    <tr style={{ borderBottom: "1px solid #ddd" }}>
                      <td style={{ padding: "16px", textAlign: "left" }}>
                        {request.player_id}
                      </td>
                      <td style={{ padding: "16px", textAlign: "left" }}>
                        {request.name}
                      </td>
                      <td style={{ padding: "16px", textAlign: "left" }}>
                        {request.country_code}
                      </td>
                      <td style={{ padding: "16px", textAlign: "left" }}>
                        {request.phone}
                      </td>
                      <td style={{ padding: "16px", textAlign: "left" }}>
                        {request.score}
                      </td>
                      <td style={{ padding: "16px", textAlign: "left" }}>
                        {request.reason_for_calling}
                      </td>
                      <td style={{ padding: "16px", textAlign: "left" }}>
                        <Button
                          variant="outlined"
                          style={{
                            padding: "8px 16px",
                            border: "none",
                            borderRadius: "8px",
                            backgroundColor: "#50868f",
                            color: "#fff",
                            cursor: "pointer",
                          }}
                          onClick={() => handleExpand(request)} // Use new function
                        >
                          {expanded === request.calling_requests_id
                            ? "Collapse"
                            : "Expand"}
                        </Button>
                      </td>
                    </tr>
                    {expanded === request.calling_requests_id && (
                      <tr>
                        <td
                          colSpan="7"
                          style={{
                            backgroundColor: "#fafafa",
                            padding: "20px",
                          }}
                        >
                          {tabValue === 0 ? (
                            <>
                              <Typography variant="h6" gutterBottom>
                                Feedback Questions
                              </Typography>

                              {request.questions_array.map(
                                (questionObj, index) => (
                                  <div
                                    key={index}
                                    style={{ marginBottom: "16px" }}
                                  >
                                    {console.log("request", request)}
                                    <Typography
                                      variant="body1"
                                      sx={{ fontWeight: 500, mb: 1 }}
                                    >
                                      {questionObj.question}
                                    </Typography>
                                    <TextField
                                      fullWidth
                                      multiline
                                      rows={2}
                                      variant="outlined"
                                      value={
                                        answers[request.calling_requests_id]?.[
                                          index
                                        ] || ""
                                      }
                                      onChange={(e) =>
                                        handleAnswerChange(
                                          request.calling_requests_id,
                                          index,
                                          e.target.value,
                                        )
                                      }
                                    />
                                  </div>
                                ),
                              )}

                              <TextField
                                select
                                fullWidth
                                label="Status"
                                value={
                                  expanded === request.calling_requests_id
                                    ? response
                                    : request.status || "pending"
                                }
                                onChange={(e) => setResponse(e.target.value)}
                                sx={{ mb: 2, mt: 2 }}
                              >
                                <MenuItem value="completed">Completed</MenuItem>
                                <MenuItem value="pending">Pending</MenuItem>
                                <MenuItem value="out_of_coverage">
                                  Out of Coverage
                                </MenuItem>
                                <MenuItem value="not_available">
                                  Not Available
                                </MenuItem>
                                <MenuItem value="not_answering">
                                  Not Answering
                                </MenuItem>
                                <MenuItem value="switched_off">
                                  Switched Off
                                </MenuItem>
                                <MenuItem value="call_busy">Call Busy</MenuItem>
                                <MenuItem value="busy">Busy</MenuItem>
                              </TextField>

                              <Button
                                variant="contained"
                                color="primary"
                                style={{
                                  padding: "8px 16px",
                                  border: "none",
                                  borderRadius: "8px",
                                  backgroundColor: "#50868f",
                                  color: "#fff",
                                  cursor: "pointer",
                                }}
                                onClick={() => handleSubmitFeedback(request)}
                              >
                                Submit Feedback
                              </Button>
                            </>
                          ) : (
                            <div>
                              <Typography variant="h6" gutterBottom>
                                Submitted Feedback
                              </Typography>
                              {request.questions_array.map(
                                (questionObj, index) => (
                                  <div
                                    key={index}
                                    style={{ marginBottom: "12px" }}
                                  >
                                    <Typography
                                      variant="body1"
                                      sx={{ fontWeight: 500 }}
                                    >
                                      {questionObj.question}
                                    </Typography>
                                    <Typography variant="body1" sx={{ ml: 2 }}>
                                      {questionObj.feedback ||
                                        "No answer provided"}
                                    </Typography>
                                    {questionObj.time && (
                                      <Typography
                                        variant="caption"
                                        sx={{ ml: 2, color: "text.secondary" }}
                                      >
                                        {new Date(
                                          questionObj.time,
                                        ).toLocaleString()}
                                      </Typography>
                                    )}
                                  </div>
                                ),
                              )}
                              <Typography variant="body1" sx={{ mt: 2 }}>
                                <strong>Status:</strong> {request.status}
                              </Typography>
                            </div>
                          )}
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div style={{ marginTop: "20px", textAlign: "center" }}>
            <button
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage === 1}
              style={{
                padding: "8px 16px",
                borderRadius: "8px",
                border: "none",
                backgroundColor: "#50868f",
                color: "#fff",
                cursor: "pointer",
                marginRight: "10px",
              }}
            >
              ◀️ Prev
            </button>
            <span style={{ fontSize: "16px", fontWeight: "500" }}>
              Page {currentPage} of {totalPages}
            </span>
            <button
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage >= totalPages}
              style={{
                padding: "8px 16px",
                borderRadius: "8px",
                border: "none",
                backgroundColor: "#50868f",
                color: "#fff",
                cursor: "pointer",
                marginLeft: "10px",
              }}
            >
              Next ▶️
            </button>
          </div>
        </>
      )}
    </div>
  );
};

export default CallingRequestsPage;
